// 资讯详情页
import {Toast} from 'vant';
import logger, {dataLogger} from '@/utils/logger';
import {useDeviceInfo} from '@/hooks/useDeviceInfo';
import {SUCCESS_CODE} from './constants';
import useRequest, {processAfterRequest} from '.';

const PATH_PREFIX = process.env.NODE_ENV !== 'mock' ? '' : '/information';

// 资讯详情
export const useNewsDetail = (informationId: number) =>
  useRequest<{
    code: number;
    message: string | null;
    data: InfoItem;
  }>({
    url: `${PATH_PREFIX}/news/newsDetail`,
    method: 'post',
    data: {
      informationId, // 资讯Id,
      hasRead: true, // 是否阅读加1
      inApp: 0, // 是否站内app 1-是, 0-不是(返回全部信息)
      deviceId: useDeviceInfo().did, // 设备id必传, 因为要返回收藏信息
    },
    afterRequest(data) {
      if (data?.code === SUCCESS_CODE || data?.code === 1002) {
        dataLogger(data);
        return data;// 注意返回的数据会自动取里面的 data
      }
      logger('后端返回错误信息:', '#d85542', data);
      throw new Error();
    },
    useNative: true,
  });

// 获取资讯内容详情
export const useRecommend = ({informationId, cmsCategoryId}: {
  informationId: number;
  cmsCategoryId: number;
}) =>
  useRequest<{
      newsRecommend: InfoItem[];
    }>({
      url: `${PATH_PREFIX}/news/newsDetailRecommend`,
      data: {
        informationId,
        newsCategoryIds: [cmsCategoryId || 0],
        hasTop: false,
        appType: useDeviceInfo().p_client
      },
      method: 'post',
    });

// 获取资讯内容详情
export const useContent = (url: string) =>
  useRequest<unknown>({
    url,
    method: 'get',
    type: 'await',
    afterRequest(data) {
      if (data) {
        dataLogger(data);
        return data;// 注意返回的数据会自动取里面的 data
      }
      logger('后端返回错误信息:', '#d85542', `${data}`);
      throw new Error();
    }
  });

enum OperateType {
  SHARE = 1, // 分享
  REPORT = 2, // 举报
  COLLECT = 3, // 收藏
  CANCEL_COLLECT = 4, // 取消收藏
}

enum ShareAppType {
  UNKNOWN = 0,
  WECHAT = 1,
  QQ = 2,
  GAOTU_APP = 3,
}

// 分享, 举报, 收藏
const useDetailOperate = (
  informationId: number,
  type: OperateType,
  afterRequest: any,
  shareApp: ShareAppType = ShareAppType.UNKNOWN
) =>
  useRequest<{
    code: number;
    message?: string;
    data?: Record<string, never>;// 移除收藏影响的行数
  }>({
    url: `${PATH_PREFIX}/news/newsDetailOperate`,
    method: 'post',
    data: {
      informationId, // 资讯Id, // 必填
      type, // 1-分享 2-举报 3-收藏 4-取消收藏
      shareApp, // 1：微信，2:qq ，3:高途App // 非必填，不传后端默认传0不知道来源
      deviceId: useDeviceInfo().did, // 设备id必传, 因为要收藏. 端外打开没有 did
    },
    afterRequest
  });

// 分享
export const useShare = (informationId: number) =>
  useDetailOperate(
    informationId,
    OperateType.SHARE,
    processAfterRequest(),
    ShareAppType.WECHAT
  );

// 举报
export const useReport = (informationId: number) =>
  useDetailOperate(
    informationId,
    OperateType.REPORT,
    processAfterRequest({
      successInfo: '举报成功'
    })
  );

// 收藏
export const useCollect = (informationId: number, successCallback: () => void) =>
  useDetailOperate(
    informationId,
    OperateType.COLLECT,
    processAfterRequest({
      successCallback,
      errorCallback(code: number, msg: string) {
        if (code === 1002 || code === 1003) {
          Toast(msg);
        }
      }
    })
  );

// 取消收藏
export const useCancelCollect = (informationId: number, successCallback: () => void) =>
  useDetailOperate(
    informationId,
    OperateType.CANCEL_COLLECT,
    processAfterRequest({
      successCallback,
      errorInfo: '取消收藏失败'
    })
  );
